import { useCallback, useEffect, useRef, useState } from "react";
import { logger } from "@platform/browser-logger";
import { useApi } from "@platform/api";

import { submitEvent } from "../utils/tracking";
import { mapLocToPolicyDetailsVariant } from "../utils";

import { useToastNotificationService } from "./index";

function usePolicyData(setCoverage, setPolicyDetailsConfig) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [data, setData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const { createToastNotification } = useToastNotificationService();

  const api = useApi();

  const fetchData = async (carrierId, policyId) => {
    setIsLoading(true);
    setError(null);

    try {
      const { data } = await api.post("/brokerportal/v1/findPolicy", {
        carrierId,
        policyId,
        forceRefresh: true,
      });

      if (data) {
        setData(data);
        setIsLoading(false);
      } else {
        setError("no-data");
      }
    } catch (error) {
      if (error.response?.data?.errorMessage === "error retrieving policy") {
        logger.error("error", error.response?.data?.errorMessage);

        setError("no-data");
        setData({});
      } else {
        setCoverage(null);
        setIsDialogOpen(false);
        createToastNotification({
          severity: "error",
          title: "Could not retrieve Policy Details",
          message: "Please try again later",
        });
      }
      submitEvent("Error Viewed", {
        error_type: "policy_details_retrieval",
        error_reason: error,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Read through a ref so openDialog stays referentially stable: fetchData
  // closes over context values (toasts) that change identity across renders,
  // and consumers key effects on openDialog.
  const fetchDataRef = useRef(fetchData);
  useEffect(() => {
    fetchDataRef.current = fetchData;
  });

  const openDialog = useCallback(
    async (carrierId, policyId, lineOfCoverage, carrierName) => {
      setIsDialogOpen(true);
      const variant = mapLocToPolicyDetailsVariant(lineOfCoverage);
      const config = {
        groupNumber: policyId,
        carrierName,
        variant,
      };
      setPolicyDetailsConfig(config);
      fetchDataRef.current(carrierId, policyId);
    },
    [setPolicyDetailsConfig],
  );

  const closeDialog = () => {
    setCoverage(null);
    setError(null);
    setPolicyDetailsConfig(null);
    setIsDialogOpen(false);
  };

  return {
    isDialogOpen,
    data,
    isLoading,
    error,
    openDialog,
    closeDialog,
    fetchData,
  };
}

export default usePolicyData;
